import { useState } from "react";
import { Check, X } from "lucide-react";
import { useLang, WHATSAPP_URL } from "../i18n";
import { Reveal } from "./Reveal";
import { WhatsAppIcon } from "./Logo";

export const Pricing = () => {
  const { t } = useLang();
  const [mode, setMode] = useState("daily");

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute left-1/2 top-16 h-80 w-[46rem] -translate-x-1/2 rounded-full bg-emerald-500/8 blur-[120px]" />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
        <Reveal className="text-center">
          <p className="font-mono2 text-xs uppercase tracking-[0.28em] text-emerald-400">{t.pricing.kicker}</p>
          <h2 className="font-display mx-auto mt-4 max-w-3xl text-3xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            {t.pricing.title}
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">{t.pricing.sub}</p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass relative mx-auto mt-12 max-w-2xl overflow-hidden rounded-3xl p-8 text-center sm:p-10">
            <div className="absolute -top-16 left-1/2 h-40 w-72 -translate-x-1/2 rounded-full bg-emerald-500/15 blur-3xl" />
            <div className="relative">
              <div className="mb-6 inline-flex rounded-full border border-slate-700/70 bg-slate-900/70 p-1">
                {["daily", "monthly"].map((m) => (
                  <button
                    key={m}
                    data-testid={`pricing-toggle-${m}`}
                    onClick={() => setMode(m)}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                      mode === m ? "bg-emerald-500 text-slate-950" : "text-slate-400 hover:text-slate-100"
                    }`}
                  >
                    {m === "daily" ? "2 CHF" : "60 CHF"} {t.pricing[m]}
                  </button>
                ))}
              </div>
              <p className="font-display text-7xl font-black tracking-tight sm:text-8xl">
                <span className="text-gradient">{mode === "daily" ? "2" : "60"}</span>
                <span className="text-slate-50 text-3xl sm:text-4xl"> CHF</span>
              </p>
              <p className="mt-2 font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">
                {t.pricing[mode]}
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-cta-btn"
                className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 px-8 py-4 text-base font-bold text-slate-950 shadow-[0_0_40px_rgba(16,185,129,0.4)] transition-transform hover:scale-105"
              >
                <WhatsAppIcon className="h-5 w-5" />
                {t.pricing.cta}
              </a>
              <p className="mt-4 text-xs text-slate-500">{t.pricing.note}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-14 overflow-hidden rounded-2xl border border-slate-800" data-testid="pricing-comparison-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/60">
                  <th className="px-5 py-4 font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {t.pricing.cols.metric}
                  </th>
                  <th className="px-5 py-4 font-display text-sm font-bold text-emerald-300">
                    {t.pricing.cols.gwd}
                  </th>
                  <th className="px-5 py-4 font-display text-sm font-bold text-slate-400">
                    {t.pricing.cols.rec}
                  </th>
                </tr>
              </thead>
              <tbody>
                {t.pricing.rows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-slate-800/60 transition-colors last:border-0 hover:bg-slate-900/40"
                  >
                    <td className="px-5 py-4 font-medium text-slate-300">{row[0]}</td>
                    <td className="px-5 py-4 text-slate-100">
                      <span className="inline-flex items-center gap-2">
                        <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                        {row[1]}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-500">
                      <span className="inline-flex items-center gap-2">
                        <X className="h-4 w-4 shrink-0 text-slate-600" />
                        {row[2]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
